/**
 * Sprint 18: Analytics Q&A Panel
 * 
 * Natural language → suggested chart + filters JSON → deep-link to pre-filtered dashboard
 * Uses GPT-4o if key present; mock fallback otherwise.
 * Logs usage telemetry (no PII/prompts).
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageSquare, Send, Sparkles, BarChart3, LineChart, PieChart,
  TrendingUp, AlertTriangle, Users, DollarSign, Calendar, Target,
  ExternalLink, X, Loader2, History, Lightbulb
} from 'lucide-react';
import { api } from '../api/client';

const EXAMPLE_QUERIES = [
  "Show me projects that are behind schedule",
  "What's the budget variance across portfolios?",
  "Which teams have the highest risk scores?",
  "Compare delivery throughput by quarter",
  "Show demand intake trends over the last 6 months",
  "What's the SLA compliance rate by department?"
];

const AnalyticsQAPanel = ({ onNavigate, className = '' }) => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Load history from localStorage
    const saved = localStorage.getItem('analytics_qa_history');
    if (saved) {
      try {
        setHistory(JSON.parse(saved).slice(0, 10));
      } catch (e) {
        console.error('Failed to parse Q&A history');
      }
    }
  }, []);

  const saveToHistory = (q, r) => {
    const newHistory = [{ query: q, result: r, timestamp: new Date().toISOString() }, ...history].slice(0, 10);
    setHistory(newHistory);
    localStorage.setItem('analytics_qa_history', JSON.stringify(newHistory));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim() || loading) return;

    setLoading(true);
    setResult(null);

    try {
      const response = await api.analyticsQA({ query: query.trim() });
      const data = response.data;
      setResult(data);
      saveToHistory(query.trim(), data);
      
      // Log telemetry (no PII)
      try {
        await api.logTelemetry({
          event: 'analytics_qa_query',
          success: data.success,
          latency_ms: data.latency_ms || 0,
          suggestion_type: data.suggestion?.chart_type || 'unknown'
        });
      } catch (telError) {
        console.debug('Telemetry logging failed:', telError);
      }
    } catch (error) {
      setResult({
        success: false,
        error: error.response?.data?.detail || 'Failed to process query',
        suggestion: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setQuery(example);
    inputRef.current?.focus();
  };

  const handleNavigate = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      window.location.href = path;
    }
  };

  const getChartIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'bar': return <BarChart3 className="w-4 h-4" />;
      case 'line': return <LineChart className="w-4 h-4" />;
      case 'pie': return <PieChart className="w-4 h-4" />;
      case 'trend': return <TrendingUp className="w-4 h-4" />;
      default: return <BarChart3 className="w-4 h-4" />;
    }
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className={`flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg ${className}`}
        data-testid="analytics-qa-expand"
      >
        <Sparkles className="w-5 h-5" />
        <span className="font-medium">Ask Analytics</span>
        <MessageSquare className="w-4 h-4 opacity-70" />
      </button>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-purple-200 shadow-lg overflow-hidden ${className}`} data-testid="analytics-qa-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold">Analytics Q&A</span>
          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">AI-Powered</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white"
            title="Query History"
          >
            <History className="w-4 h-4" />
          </button>
          <button
            onClick={() => setExpanded(false)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* History Panel */}
      {showHistory && history.length > 0 && (
        <div className="border-b border-purple-100 bg-purple-50 p-3 max-h-40 overflow-y-auto">
          <p className="text-xs text-purple-700 font-medium mb-2">Recent Queries</p>
          <div className="space-y-1">
            {history.map((h, idx) => (
              <button
                key={idx}
                onClick={() => { setQuery(h.query); setShowHistory(false); }}
                className="w-full text-left text-sm text-purple-800 hover:bg-purple-100 px-2 py-1 rounded truncate"
              >
                {h.query}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-b border-gray-100">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask about your portfolio data..."
            className="w-full px-4 py-2.5 pr-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 focus:border-purple-400 text-sm"
            disabled={loading}
            data-testid="qa-input"
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-purple-600 hover:bg-purple-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>

      {/* Example Queries */}
      {!result && (
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-yellow-500" />
            <span className="text-xs font-medium text-gray-600">Try asking:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_QUERIES.slice(0, 4).map((example, idx) => (
              <button
                key={idx}
                onClick={() => handleExampleClick(example)}
                className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:border-purple-300 hover:bg-purple-50 transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="p-4">
          {result.success && result.suggestion ? (
            <div className="space-y-4">
              {/* Suggested Chart */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getChartIcon(result.suggestion.chart_type)}
                    <span className="font-medium text-purple-900">{result.suggestion.title}</span>
                  </div>
                  <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                    {result.suggestion.chart_type} chart
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{result.suggestion.description}</p>
                
                {/* Suggested Filters */}
                {result.suggestion.filters && Object.keys(result.suggestion.filters).length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Suggested Filters:</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(result.suggestion.filters).map(([key, value]) => (
                        <span key={key} className="text-xs bg-white px-2 py-1 rounded border border-gray-200">
                          {key}: <span className="font-medium">{value}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Deep Link */}
                {result.suggestion.deep_link && (
                  <button
                    onClick={() => handleNavigate(result.suggestion.deep_link)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                    data-testid="qa-deep-link"
                  >
                    <span>Open Chart</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* AI Explanation */}
              {result.explanation && (
                <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  <p className="font-medium text-gray-700 mb-1">AI Analysis:</p>
                  <p>{result.explanation}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-gray-600">{result.error || 'Could not generate suggestion'}</p>
              <button
                onClick={() => setResult(null)}
                className="mt-3 text-sm text-purple-600 hover:underline"
              >
                Try another query
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-6 text-center">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-600">Analyzing your question...</p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsQAPanel;
